import { OrgId } from "@hex/common";
import { rgba } from "polished";
import React from "react";
import styled from "styled-components";

import { HexCallout } from "../../hex-components/HexCallout.js";
import { useToggleState } from "../../hooks/useToggleState";
import { GLOBAL_ORG_ID } from "../../orgs";
import { useOrgDetailsQuery } from "../../route/LoginRoute.generated";
import { Routes, useQueryParams } from "../../route/routes";
import { allowGlobalSso } from "../../util/data";
import { LoadingView } from "../../views/LoadingView";
import { LightbulbIcon } from "../icons/CustomIcons";
import { CornerAccent } from "../icons/PlansIcons";

import { AzureLogin } from "./AzureLogin";
import { EmailLogin } from "./EmailLogin";
import { GoogleLogin } from "./GoogleLogin";
import { SsoLogin } from "./SsoLogin";

const TopLeftAccent = styled(CornerAccent)`
  width: 13px;
  height: 13px;
  position: absolute;
  top: -1.5px;
  left: -1.5px;
  svg {
    opacity: 0.7;
    color: ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  }
`;

const TopRightAccent = styled(CornerAccent)`
  width: 13px;
  height: 13px;
  transform: rotate(90deg);
  position: absolute;
  top: -1.5px;
  right: -1.5px;
  svg {
    opacity: 0.7;
    color: ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  }
`;

const BottomLeftAccent = styled(CornerAccent)`
  width: 13px;
  height: 13px;
  transform: rotate(270deg);
  position: absolute;
  bottom: -1.5px;
  left: -1.5px;
  svg {
    opacity: 0.7;
    color: ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  }
`;

const BottomRightAccent = styled(CornerAccent)`
  width: 13px;
  height: 13px;
  transform: rotate(180deg);
  position: absolute;
  bottom: -1.5px;
  right: -1.5px;
  svg {
    opacity: 0.7;
    color: ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  }
`;

const LoginCardWrapper = styled.div`
  position: relative;
  padding: 25px;

  background-color: ${({ theme }) => theme.backgroundColor.MUTED};
  box-shadow: ${({ theme }) => theme.boxShadow.CARD};
`;

export const LoginCardLoading = styled(LoadingView)`
  position: absolute;
  top: 0;
  left: 0;

  background-color: ${({ theme }) =>
    rgba(theme.marketingColors.BACKGROUND, 0.2)};

  transition: background-color 0.5s ease;
`;

export const LoginButtonContainer = styled.div`
  margin-bottom: 10px;
`;

export const EmailFormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 17px 0;

  > span {
    margin: 0 10px;

    opacity: 0.5;
  }

  &::before,
  &::after {
    flex: 1 1 auto;

    border-top: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};

    content: "";
  }
`;

export const LoginCardContainer = React.memo(function LoginCardContent({
  children,
  className,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <LoginCardWrapper className={className}>
      {children}
      <TopLeftAccent />
      <TopRightAccent />
      <BottomLeftAccent />
      <BottomRightAccent />
    </LoginCardWrapper>
  );
});

const AdminWarningDiv = styled(HexCallout)`
  margin-bottom: 16px;

  color: ${({ theme }) => theme.fontColor.MUTED};
`;

interface LoginCardProps {
  orgId: OrgId | undefined;
  authQueryParams: Record<string, string>;
  embedded: boolean;
}

export const LoginCard: React.ComponentType<LoginCardProps> = React.memo(
  function LoginCard({ authQueryParams, embedded, orgId }) {
    const queryParams = useQueryParams();

    const { data, loading } = useOrgDetailsQuery({
      variables: { orgId: orgId ?? GLOBAL_ORG_ID },
    });

    const [
      userSubmitted,
      ,
      { setFalse: authnRequestCompleted, setTrue: authnRequested },
    ] = useToggleState(false);

    const ssoEnforced = data?.publicOrgDetails?.ssoEnforced ?? false;

    // Admin break-glass mode: when ?password=true is present, show only the password
    // login form and hide all other login options (SSO, Google, Azure). This is an
    // undiscoverable escape hatch for admins — not linked anywhere in the UI.
    // Note: password login failures also redirect with ?password=true to keep the user
    // in breakglass mode for retry (see EmailLogin.tsx onSubmitPassword).
    // TODO: Remove when password login is fully deprecated and all users migrate to magic link or OAuth.
    const showAdminBreakglassForm = !!queryParams.get("password");

    const useEmbeddedRedirect =
      data?.publicOrgDetails?.ssoEnforced &&
      data.publicOrgDetails.allowsIframing;

    if (embedded && !useEmbeddedRedirect) {
      authQueryParams["redirectTo"] = Routes.LOGIN_SUCCESS.href(
        orgId ?? GLOBAL_ORG_ID,
        false,
        {},
      );
    }

    if (data == null || data.publicOrgDetails == null) {
      if (loading) {
        return null;
      } else {
        return (
          <LoginCardContainer
            css={`
              text-align: center;
            `}
          >
            We couldn&apos;t find this workspace
          </LoginCardContainer>
        );
      }
    }

    const { azureAuth, googleAuth, ssoEnabled } = data.publicOrgDetails;

    // Google SSO is not supported in iOS WebViews
    const googleSsoSupported = !(
      /linkedin|instagram|twitter|messengerforios/i.test(
        navigator.userAgent.toLowerCase(),
      ) && navigator.userAgent.includes("iPhone")
    );

    return (
      <>
        {!googleSsoSupported && (
          <HexCallout
            $size="small"
            css={`
              margin-bottom: 16px;
            `}
            intent="warning"
          >
            Looks like you&apos;re in an in-app web browser. For the best
            experience (including login with Google) we recommend navigating to
            app.hex.tech/signup from your device&apos;s browser application
            (e.g. Chrome).
          </HexCallout>
        )}
        {showAdminBreakglassForm && ssoEnforced && (
          <AdminWarningDiv $size="small" icon={<LightbulbIcon />}>
            Only workspace admins can log in with password.
          </AdminWarningDiv>
        )}
        <LoginCardContainer
          css={`
            position: relative;
          `}
        >
          {!showAdminBreakglassForm &&
            ssoEnabled &&
            (orgId || allowGlobalSso) && (
              <LoginButtonContainer>
                <SsoLogin
                  authQueryParams={authQueryParams}
                  embedded={embedded}
                  orgId={orgId ?? GLOBAL_ORG_ID}
                  onComplete={authnRequestCompleted}
                  onSubmit={authnRequested}
                />
              </LoginButtonContainer>
            )}
          {!showAdminBreakglassForm &&
            !ssoEnforced &&
            googleAuth &&
            googleSsoSupported && (
              <LoginButtonContainer>
                <GoogleLogin
                  authQueryParams={authQueryParams}
                  embedded={embedded}
                  orgId={orgId}
                  onComplete={authnRequestCompleted}
                  onSubmit={authnRequested}
                />
              </LoginButtonContainer>
            )}
          {!showAdminBreakglassForm && !ssoEnforced && azureAuth && (
            <LoginButtonContainer>
              <AzureLogin
                authQueryParams={authQueryParams}
                embedded={embedded}
                orgId={orgId}
                onComplete={authnRequestCompleted}
                onSubmit={authnRequested}
              />
            </LoginButtonContainer>
          )}
          {(!ssoEnforced || showAdminBreakglassForm) && (
            <>
              {!showAdminBreakglassForm && (googleAuth || azureAuth) && (
                <EmailFormHeader>
                  <span>or log in with email</span>
                </EmailFormHeader>
              )}
              <EmailLogin
                authQueryParams={authQueryParams}
                embedded={embedded}
                orgId={orgId}
                onComplete={authnRequestCompleted}
                onSubmit={authnRequested}
              />
            </>
          )}
          {userSubmitted && <LoginCardLoading />}
        </LoginCardContainer>
      </>
    );
  },
);
